import { FunctionComponent, createContext } from 'preact';
import { useContext, useEffect, useMemo, useState } from 'preact/hooks';

import { useApis } from './ApisProvider';
import { useCurrentAsset } from './CurrentAssetProvider';
import { useAbortController } from './useAbortController';
import { AccessDefinitionEntity } from '../services/svpApi';

interface AccessDefinitionsContextProps {
  accessDefinitions: AccessDefinitionEntity[];
}

const AccessDefinitionsContext = createContext<AccessDefinitionsContextProps>(
  undefined!
);

export const AccessDefinitionsProvider: FunctionComponent = ({ children }) => {
  const [accessDefinitions, setAccessDefinitions] = useState<
    AccessDefinitionEntity[]
  >([]);

  const { asset } = useCurrentAsset();
  const { svpApi } = useApis();
  const { getSignal } = useAbortController();

  useEffect(() => {
    setAccessDefinitions([]);

    if (!asset) {
      return;
    }

    svpApi.assets
      .getAccessDefinitions({ signal: getSignal() })
      .then(setAccessDefinitions)
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        console.error(err);
      });
  }, [asset, svpApi, getSignal]);

  const state = useMemo(() => {
    return {
      accessDefinitions,
    };
  }, [accessDefinitions]);

  return (
    <AccessDefinitionsContext.Provider value={state}>
      {children}
    </AccessDefinitionsContext.Provider>
  );
};

export const useAccessDefinitions = () => {
  const accessDefinitions = useContext(AccessDefinitionsContext);

  if (process.env.NODE_ENV !== 'production' && !accessDefinitions) {
    console.warn(
      'AccessDefinitions context is undefined, please verify you are calling useAccessDefinitions() as child of a <AccessDefinitionsProvider> component.'
    );
  }

  return accessDefinitions;
};
